import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import { Grid, IconButton, Paper, Tooltip } from "@mui/material"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { FormNumberInput } from "../../../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { SelectObjectInput } from "../../../../../../../../../../../components/inputs/select-input/SelectObjectInput"
import { AssignedParking } from "../../../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/AssignedParking"
import { BSParking } from "../../../../../../../../../../../core/dto/beem-shot/BSParking/BSParking"
import { stringToNumber } from "../../../../../../../../../../../core/services/helper-service"
import { BSParkingListContext } from "../../../../BSBuildingParkingPage/context/BSParkingListContext"
import { BSLevelContext } from "../../../context/BSLevelContext"
import { GroupLevel } from "../../../context/GroupLevel"
import { ParkingAssignmentContext } from "../../../context/ParkingAssignmentContext"
import { BSLevelFormContext } from "../../../context/BSLevelFormContext"

interface IProps {
  groupLevel: GroupLevel
  setDisplayParkingOptions: React.Dispatch<React.SetStateAction<boolean>>
  parkingPercentageSum: number
}

export function ParkingAssignmentForm({
  groupLevel,
  setDisplayParkingOptions,
  parkingPercentageSum,
}: Readonly<IProps>): React.JSX.Element {
  const { superstructureParkingList, infrastructureParkingList } = useContext(BSParkingListContext)
  const { setIsLevelUpdated } = useContext(BSLevelContext)
  const { setFormFieldValue } = useContext(BSLevelFormContext)
  const {
    parkingErrors,
    fieldValidators,
    superstructureOptions,
    infrastructureOptions,
    setParkingErrors,
    validateParkingAssignment,
  } = useContext(ParkingAssignmentContext)

  const [newAffectedParking, setNewAffectedParking] = useState<AssignedParking>(() => ({
    parkingId: "",
    percentage: 0,
  }))

  const isSuperstructure = useMemo(() => groupLevel.levelList[0].superstructure, [groupLevel.levelList])

  const selectedParking: BSParking | undefined = useMemo(
    () =>
      isSuperstructure
        ? superstructureParkingList.find((parking) => parking.id === newAffectedParking.parkingId)
        : infrastructureParkingList.find((parking) => parking.id === newAffectedParking.parkingId),
    [isSuperstructure, superstructureParkingList, infrastructureParkingList, newAffectedParking.parkingId]
  )

  const handleFieldChange: (field: keyof AssignedParking, value: number | string) => void = useCallback(
    (field, value) => {
      setNewAffectedParking((prev) => ({ ...prev, [field]: value }))
      fieldValidators[field]?.(field, value, parkingPercentageSum)
    },
    [fieldValidators, parkingPercentageSum]
  )

  const handleClose: () => void = useCallback(() => {
    setDisplayParkingOptions(false)
    setParkingErrors({})
  }, [setDisplayParkingOptions, setParkingErrors])

  const handleConfirm = useCallback(() => {
    if (validateParkingAssignment(newAffectedParking, parkingPercentageSum)) {
      const updatedAffectedParkingList = [...(groupLevel.levelList[0].bsParkingList ?? []), newAffectedParking]

      setFormFieldValue("bsParkingList", updatedAffectedParkingList, groupLevel.id, isSuperstructure)

      setDisplayParkingOptions(false)
      setIsLevelUpdated(true)
    }
  }, [
    groupLevel.id,
    groupLevel.levelList,
    isSuperstructure,
    newAffectedParking,
    parkingPercentageSum,
    setDisplayParkingOptions,
    setFormFieldValue,
    setIsLevelUpdated,
    validateParkingAssignment,
  ])

  // force parking percentage field in form only for infrastructure levels
  useEffect(() => {
    if (!isSuperstructure) {
      setNewAffectedParking((prev) => ({ ...prev, percentage: 100 }))
    }
  }, [isSuperstructure])

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        py: 1,
        px: 2,
        borderRadius: 4,
        boxShadow: "0px 1px 3px rgba(141, 83, 83, 0.1)",
      }}>
      <Grid container alignItems="center" columnSpacing={1}>
        <Grid container item xs={11} columnSpacing={1}>
          <Grid item xs={8}>
            <SelectObjectInput
              fieldName="parkingId"
              label="Parking à affecter"
              selectedOption={selectedParking}
              options={isSuperstructure ? superstructureOptions : infrastructureOptions}
              handleChange={(value) => value?.id && handleFieldChange("parkingId", value.id)}
              errors={parkingErrors}
            />
          </Grid>
          <Grid item xs={4}>
            <Tooltip title="par rapport à la surface du groupe" arrow placement="right">
              <div>
                <FormNumberInput
                  fieldName="percentage"
                  label="Pourcentage (%)"
                  value={isSuperstructure ? newAffectedParking.percentage ?? 0 : 100}
                  handleChange={(value) => handleFieldChange("percentage", stringToNumber(value))}
                  errors={parkingErrors}
                  disabled={!isSuperstructure}
                />
              </div>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid container item xs={1} columnSpacing={1}>
          <Grid item xs={6}>
            <IconButton
              color="error"
              onClick={handleClose}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(255, 46, 46, 0.1)",
                },
              }}>
              <CloseIcon />
            </IconButton>
          </Grid>

          <Grid item xs={6}>
            <IconButton
              color="success"
              onClick={handleConfirm}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(46, 125, 50, 0.1)",
                },
              }}>
              <CheckIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  )
}
